import React, { useState, useEffect, useReducer, useContext, useMemo } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import InputAdornment from "@material-ui/core/InputAdornment";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import { isArray } from "lodash";

import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import AnnouncementModal from "../../components/AnnouncementModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import MetricsStrip from "../../components/MetricsStrip";

const reducer = (state, action) => {
  if (action.type === "LOAD_ANNOUNCEMENTS") {
    const announcements = action.payload;
    const newAnnouncements = [];

    if (isArray(announcements)) {
      announcements.forEach((announcement) => {
        const announcementIndex = state.findIndex(
          (currentAnnouncement) => currentAnnouncement.id === announcement.id
        );

        if (announcementIndex !== -1) {
          state[announcementIndex] = announcement;
        } else {
          newAnnouncements.push(announcement);
        }
      });
    }

    return [...state, ...newAnnouncements];
  }

  if (action.type === "UPDATE_ANNOUNCEMENTS") {
    const announcement = action.payload;
    const announcementIndex = state.findIndex(
      (currentAnnouncement) => currentAnnouncement.id === announcement.id
    );

    if (announcementIndex !== -1) {
      state[announcementIndex] = announcement;
      return [...state];
    }

    return [announcement, ...state];
  }

  if (action.type === "DELETE_ANNOUNCEMENT") {
    const announcementId = action.payload;
    const announcementIndex = state.findIndex(
      (currentAnnouncement) => currentAnnouncement.id === announcementId
    );

    if (announcementIndex !== -1) {
      state.splice(announcementIndex, 1);
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    minWidth: 0,
  },
  contentStack: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
  },
  hero: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  heroActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  heroTitle: {
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  summaryCard: {
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 16,
    minHeight: 92,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  summaryLabel: {
    fontSize: "0.74rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.7,
  },
  summaryValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1.1,
    marginTop: theme.spacing(0.75),
  },
  filtersPaper: {
    width: "100%",
    minWidth: 0,
    padding: theme.spacing(1.5),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  filtersTitle: {
    fontWeight: 600,
  },
  filtersActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "stretch",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  tableSearch: {
    width: 320,
    maxWidth: "100%",
  },
  tablePaper: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  tableHeaderTitle: {
    fontWeight: 600,
  },
  tableContainer: {
    width: "100%",
    minWidth: 0,
    maxHeight: "68vh",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  tableHeadCell: {
    fontWeight: 600,
  },
  actionsCell: {
    whiteSpace: "nowrap",
  },
  helperText: {
    opacity: 0.72,
    marginTop: theme.spacing(0.5),
  },
  loadMoreBox: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const getPriorityMeta = (priority) => {
  switch (priority) {
    case 1:
      return {
        label: "Alta",
        backgroundColor: "#fee2e2",
        color: "#b91c1c",
      };
    case 2:
      return {
        label: "Media",
        backgroundColor: "#fef3c7",
        color: "#92400e",
      };
    case 3:
      return {
        label: "Baixa",
        backgroundColor: "#dbeafe",
        color: "#1d4ed8",
      };
    default:
      return {
        label: "-",
        backgroundColor: "#e5e7eb",
        color: "#374151",
      };
  }
};

const getStatusMeta = (status) => {
  if (status) {
    return {
      label: i18n.t("announcements.active"),
      backgroundColor: "#dcfce7",
      color: "#166534",
    };
  }

  return {
    label: i18n.t("announcements.inactive"),
    backgroundColor: "#e5e7eb",
    color: "#374151",
  };
};

const Announcements = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [deletingAnnouncement, setDeletingAnnouncement] = useState(null);
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [reloadFlag, setReloadFlag] = useState(0);
  const [searchParam, setSearchParam] = useState("");
  const [announcements, dispatch] = useReducer(reducer, []);

  useEffect(() => {
    async function fetchData() {
      if (!user.super) {
        toast.error("Esta empresa nao possui permissao para acessar essa pagina. Redirecionando.");
        setTimeout(() => {
          history.push("/");
        }, 1000);
      }
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchAnnouncements();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, reloadFlag, searchParam]);

  useEffect(() => {
    if (user.companyId) {
      const onCompanyAnnouncement = (data) => {
        if (data.action === "update" || data.action === "create") {
          dispatch({ type: "UPDATE_ANNOUNCEMENTS", payload: data.record });
        }

        if (data.action === "delete") {
          dispatch({ type: "DELETE_ANNOUNCEMENT", payload: +data.id });
        }
      };

      socket.on("company-announcement", onCompanyAnnouncement);

      return () => {
        socket.off("company-announcement", onCompanyAnnouncement);
      };
    }
  }, [socket, user]);

  const fetchAnnouncements = async () => {
    try {
      if (pageNumber === 1) {
        dispatch({ type: "RESET" });
      }

      const { data } = await api.get("/announcements/", {
        params: { searchParam, pageNumber },
      });

      dispatch({ type: "LOAD_ANNOUNCEMENTS", payload: data.records || [] });
      setHasMore(Boolean(data.hasMore));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const refreshList = () => {
    setPageNumber(1);
    setReloadFlag((current) => current + 1);
  };

  const handleOpenAnnouncementModal = () => {
    setSelectedAnnouncement(null);
    setAnnouncementModalOpen(true);
  };

  const handleCloseAnnouncementModal = () => {
    setSelectedAnnouncement(null);
    setAnnouncementModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
    setPageNumber(1);
  };

  const handleEditAnnouncement = (announcement) => {
    setSelectedAnnouncement(announcement);
    setAnnouncementModalOpen(true);
  };

  const handleDeleteAnnouncement = async (announcement) => {
    try {
      if (announcement.mediaName) {
        await api.delete(`/announcements/${announcement.id}/media-upload`);
      }

      await api.delete(`/announcements/${announcement.id}`);
      toast.success(i18n.t("announcements.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }

    setDeletingAnnouncement(null);
    setConfirmModalOpen(false);
    refreshList();
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (event) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const summary = useMemo(() => {
    const active = announcements.filter((announcement) => announcement.status).length;
    const withAttachment = announcements.filter((announcement) => Boolean(announcement.mediaName)).length;
    const highPriority = announcements.filter((announcement) => announcement.priority === 1).length;

    return {
      total: announcements.length,
      active,
      inactive: announcements.length - active,
      withAttachment,
      highPriority,
    };
  }, [announcements]);

  const summaryCards = useMemo(
    () => [
      { key: "announcements", label: "Comunicados", value: summary.total },
      { key: "active", label: "Ativos", value: summary.active },
      { key: "attachment", label: "Com anexo", value: summary.withAttachment },
      { key: "priority", label: "Alta prioridade", value: summary.highPriority },
    ],
    [summary]
  );

  return (
    <MainContainer className={classes.mainContainer} fullWidth>
      <ConfirmationModal
        title={
          deletingAnnouncement &&
          `${i18n.t("announcements.confirmationModal.deleteTitle")} ${
            deletingAnnouncement.title || deletingAnnouncement.name
          }?`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteAnnouncement(deletingAnnouncement)}
      >
        {i18n.t("announcements.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <AnnouncementModal
        resetPagination={refreshList}
        open={announcementModalOpen}
        onClose={handleCloseAnnouncementModal}
        aria-labelledby="form-dialog-title"
        announcementId={selectedAnnouncement && selectedAnnouncement.id}
      />

      <div className={classes.contentStack}>
        <Box className={classes.hero}>
          <Box>
            <Typography variant="h5" className={classes.heroTitle}>
              {i18n.t("announcements.title")}
            </Typography>
          </Box>
          <Box className={classes.heroActions}>
            <Button variant="outlined" onClick={refreshList} disabled={loading}>
              Atualizar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenAnnouncementModal}
            >
              {i18n.t("announcements.buttons.add")}
            </Button>
          </Box>
        </Box>

        <MetricsStrip ariaLabel="Indicadores de comunicados" items={summaryCards} />

        <Paper className={classes.tablePaper} variant="outlined">
          <Box className={classes.tableHeader}>
            <Typography variant="body1" className={classes.tableHeaderTitle}>
              Comunicados operacionais
            </Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
              <TextField
                className={classes.tableSearch}
                variant="outlined"
                size="small"
                placeholder={i18n.t("announcements.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                inputProps={{ "aria-label": "Pesquisar comunicados" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Chip size="small" label={`${summary.total} carregados`} />
              <Chip size="small" label={`${summary.active} ativos`} />
            </Box>
          </Box>

          <TableContainer className={classes.tableContainer} onScroll={handleScroll}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("announcements.table.title")}
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("announcements.table.priority")}
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("announcements.table.mediaName")}
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("announcements.table.status")}
                  </TableCell>
                  <TableCell align="center" className={classes.tableHeadCell}>
                    {i18n.t("announcements.table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {announcements.map((announcement) => {
                  const priorityMeta = getPriorityMeta(announcement.priority);
                  const statusMeta = getStatusMeta(announcement.status);

                  return (
                    <TableRow key={announcement.id} hover>
                      <TableCell align="center">{announcement.title}</TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={priorityMeta.label}
                          style={{
                            backgroundColor: priorityMeta.backgroundColor,
                            color: priorityMeta.color,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {announcement.mediaName ?? i18n.t("quickMessages.noAttachment")}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={statusMeta.label}
                          style={{
                            backgroundColor: statusMeta.backgroundColor,
                            color: statusMeta.color,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" className={classes.actionsCell}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditAnnouncement(announcement)}
                        >
                          <EditIcon />
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={() => {
                            setConfirmModalOpen(true);
                            setDeletingAnnouncement(announcement);
                          }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {loading && <TableRowSkeleton columns={5} />}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && announcements.length === 0 && (
            <Box p={4} textAlign="center">
              <Typography variant="h6">Nenhum comunicado encontrado</Typography>
              <Typography variant="body2" className={classes.helperText}>
                Ajuste a busca ou cadastre um novo comunicado para continuar.
              </Typography>
            </Box>
          )}

          {!loading && hasMore && announcements.length > 0 && (
            <div className={classes.loadMoreBox}>
              <Button variant="outlined" color="primary" onClick={loadMore}>
                Carregar mais comunicados
              </Button>
            </div>
          )}
        </Paper>
      </div>
    </MainContainer>
  );
};

export default Announcements;
